import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapboxMapProps {
  locations: {
    id: string;
    name: string;
    lat: number;
    lng: number;
    sentiment: string;
    // Optionally, stores: { name: string; hash_id: string }[]
    stores?: { name: string; hash_id: string }[];
    avgSentiment?: string;
  }[];
  viewport: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  calculateStateColor: (stateName: string) => string;
  getMarkerColor: (sentiment: string) => string;
  getCityPopupContent: (location: any) => string;
  onMarkerClick?: (location: any) => void;
}

const MapboxMap: React.FC<MapboxMapProps> = ({
  locations,
  viewport,
  calculateStateColor,
  getMarkerColor,
  getCityPopupContent,
  onMarkerClick,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) {
      console.error('Map container not found.');
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/jblockqr328/cmc3r2jcr007r01sk1pvyfvf2',
      center: [viewport.longitude, viewport.latitude],
      zoom: viewport.zoom,
      accessToken: 'pk.eyJ1IjoiamJsb2NrcXIzMjgiLCJhIjoiY21hbjZ2bDlmMHMycDJqcHdtbW14dno5eiJ9.Gaw52qUaw3blupbLE8milw',
    });

    // Add markers to the map
    locations.forEach((location) => {
      const markerElement = document.createElement('div');
      markerElement.className = 'custom-marker';
      markerElement.style.backgroundColor = getMarkerColor(location.sentiment);
      markerElement.style.width = '20px';
      markerElement.style.height = '20px';
      markerElement.style.borderRadius = '50%';
      markerElement.style.border = '3px solid white';
      markerElement.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.5)';
      markerElement.style.cursor = 'pointer';

      new mapboxgl.Marker(markerElement)
        .setLngLat([location.lng, location.lat])
        .addTo(map);

      markerElement.onclick = (e) => {
        // Remove any existing popups first
        const existingPopups = document.querySelectorAll('.mapboxgl-popup');
        existingPopups.forEach(popup => popup.remove());
        
        // Show popup
        new mapboxgl.Popup({ 
          offset: 18,
          closeButton: true,
          closeOnClick: false,
          maxWidth: '350px'
        })
          .setLngLat([location.lng, location.lat])
          .setHTML(getCityPopupContent(location))
          .addTo(map);
        
        console.log('Popup created!', location);
        if (onMarkerClick) onMarkerClick(location);
      };
    });

    // Add state layer
    const addStateLayer = () => {
      map.on('load', () => {
        map.addSource('states', {
          type: 'geojson',
          data: 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json',
        });

        map.addLayer({
          id: 'state-borders',
          type: 'line',
          source: 'states',
          layout: {},
          paint: {
            'line-color': '#000000',
            'line-width': 1,
          },
        });
      });
    };

    addStateLayer();

    return () => map.remove();
  }, [locations, viewport, calculateStateColor, getMarkerColor, getCityPopupContent, onMarkerClick]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '500px', borderRadius: '10px' }} />;
};

export default MapboxMap;